import { useCallback, useEffect, useState } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Tag,
  Switch,
  Space,
} from 'antd';
import { KeyOutlined, PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import {
  supabase,
  usePortalPermissions,
  PermissionGate,
  resetPasswordRedirectUrl,
  SetupCredentialsNotice,
  invokeEdgeFunction,
  sendPasswordSetupLink,
  logAssistResetAudit,
  useAuthUser,
} from '@ubus/shared';
import AdminListPage from '../components/AdminListPage';
import { useAdminTableStability } from '../hooks/useAdminTableStability';
import { useTranslation } from 'react-i18next';
import { localeConfirm, localeSuccess } from '../utils/localeModal';

interface PortalUserRow {
  auth_uid: string;
  email: string;
  identity_role: string;
  display_name: string | null;
  status: string;
  role_id: string | null;
  role_code: string | null;
  role_name: string | null;
  last_sign_in_at: string | null;
  created_at: string | null;
}

interface PortalRoleOption {
  id: string;
  code: string;
  name: string;
}

function localizedRoleName(
  code: string | null | undefined,
  fallback: string | null | undefined,
  t: (key: string, options?: { defaultValue?: string }) => string
): string {
  if (code) {
    return t(`rbac.systemRoles.${code}.name`, { defaultValue: fallback ?? code });
  }
  return fallback ?? '—';
}

function UsersPage() {
  const { t } = useTranslation();
  const user = useAuthUser();
  const { hasPermission, refresh: refreshAccess } = usePortalPermissions();
  const canManage = hasPermission('system.users.manage');
  const [users, setUsers] = useState<PortalUserRow[]>([]);
  const [roles, setRoles] = useState<PortalRoleOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<PortalUserRow | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [form] = Form.useForm<{
    email: string;
    password?: string;
    display_name?: string;
    role_id: string;
  }>();
  const [editForm] = Form.useForm<{
    display_name?: string;
    role_id: string;
  }>();
  const { pagination, onTableChange, rowClassName, afterMutation } = useAdminTableStability();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: userRows, error: userErr }, { data: roleRows, error: roleErr }] =
        await Promise.all([
          supabase.rpc('list_portal_users'),
          supabase
            .from('portal_roles')
            .select('id, code, name')
            .order('name'),
        ]);
      if (userErr) throw userErr;
      if (roleErr) throw roleErr;
      setUsers((userRows as PortalUserRow[]) ?? []);
      // Platform users can only be assigned platform (non-fleet) roles.
      // Fleet system roles (fleet_manager / fleet_dispatcher / fleet_read_only)
      // are managed per-fleet-operator via Fleet Operators → Members.
      setRoles(
        ((roleRows as PortalRoleOption[]) ?? []).filter(
          (r) => !r.code.startsWith('fleet_')
        )
      );
    } catch (e) {
      message.error(e instanceof Error ? e.message : t('rbac.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleRoleChange = async (authUid: string, roleId: string) => {
    try {
      const { error } = await supabase.rpc('assign_portal_user_role', {
        p_auth_uid: authUid,
        p_role_id: roleId,
      });
      if (error) throw error;
      message.success(t('rbac.roleAssigned'));
      await afterMutation(authUid, load);
      await refreshAccess();
    } catch (e) {
      message.error(e instanceof Error ? e.message : t('rbac.saveFailed'));
      await load();
    }
  };

  const handleStatusToggle = async (row: PortalUserRow, active: boolean) => {
    try {
      const { error } = await supabase.rpc('set_portal_user_status', {
        p_auth_uid: row.auth_uid,
        p_status: active ? 'active' : 'disabled',
      });
      if (error) throw error;

      await invokeEdgeFunction('provision-user', {
        body: {
          action: 'set_status',
          user_id: row.auth_uid,
          status: active ? 'active' : 'disabled',
        },
      });

      message.success(t('rbac.statusUpdated'));
      await afterMutation(row.auth_uid, load);
    } catch (e) {
      message.error(e instanceof Error ? e.message : t('rbac.saveFailed'));
      await load();
    }
  };

  const handleSendSetupLink = (row: PortalUserRow) => {
    if (!canManage) {
      message.warning(t('rbac.setupLinkManageOnly'));
      return;
    }
    const label = row.display_name || row.email;
    localeConfirm(t, {
      title: t('rbac.sendSetupLink'),
      content: t('rbac.setupLinkConfirm', { name: label }),
      okText: t('rbac.sendSetupLink'),
      onOk: async () => {
        try {
          const result = await sendPasswordSetupLink({
            email: row.email,
            redirectTo: resetPasswordRedirectUrl(),
            sendEmail: true,
          });
          logAssistResetAudit({
            actorId: user?.id,
            targetType: 'portal_user',
            targetId: row.auth_uid,
            channel: result.emailed ? 'email_link' : 'link_only',
            emailed: result.emailed,
          });
          localeSuccess(t, {
            title: t('rbac.setupLinkSent'),
            width: 560,
            content: (
              <SetupCredentialsNotice
                email={result.email || row.email}
                setupLink={result.setupLink}
                extraNote={
                  result.emailed
                    ? t('rbac.setupLinkEmailed', {
                        email: result.recipient || row.email,
                      })
                    : t('rbac.setupLinkShareHint')
                }
              />
            ),
          });
        } catch (err) {
          message.error(
            err instanceof Error ? err.message : t('rbac.setupLinkFailed')
          );
        }
      },
    });
  };

  const handleCreateHqAdmin = async () => {
    const values = await form.validateFields();
    setCreating(true);
    try {
      const { data, error } = await invokeEdgeFunction<{ error?: string }>('provision-user', {
        body: {
          role: 'admin',
          email: values.email.trim(),
          portal_role_id: values.role_id,
          display_name: values.display_name?.trim() || undefined,
          redirect_to: resetPasswordRedirectUrl(),
        },
      });
      if (error) throw error;
      if (data && typeof data === 'object' && 'error' in data) {
        throw new Error(String((data as { error: unknown }).error));
      }
      const result = data as { email?: string; setupLink?: string | null; userId?: string };
      setCreateOpen(false);
      form.resetFields();
      localeSuccess(t, {
        title: t('rbac.hqAdminSetupTitle'),
        width: 560,
        content: (
          <SetupCredentialsNotice
            email={result.email || values.email.trim()}
            setupLink={result.setupLink}
          />
        ),
      });
      await afterMutation(result.userId, load);
    } catch (e) {
      message.error(e instanceof Error ? e.message : t('rbac.saveFailed'));
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (row: PortalUserRow) => {
    setEditingRow(row);
    editForm.setFieldsValue({
      display_name: row.display_name ?? undefined,
      role_id: row.role_id ?? undefined,
    });
    setEditOpen(true);
  };

  const handleEditSave = async () => {
    if (!editingRow) return;
    const values = await editForm.validateFields();
    setSavingEdit(true);
    try {
      const { error } = await supabase.rpc('update_platform_user', {
        p_auth_uid: editingRow.auth_uid,
        p_display_name: values.display_name?.trim() ?? '',
        p_role_id: values.role_id,
      });
      if (error) throw error;
      message.success(t('rbac.userUpdated'));
      setEditOpen(false);
      setEditingRow(null);
      await afterMutation(editingRow.auth_uid, load);
      await refreshAccess();
    } catch (e) {
      message.error(e instanceof Error ? e.message : t('rbac.saveFailed'));
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = (row: PortalUserRow) => {
    if (row.auth_uid === user?.id) {
      message.warning(t('rbac.cannotDeleteSelf'));
      return;
    }
    localeConfirm(t, {
      title: t('rbac.deleteUserTitle'),
      content: t('rbac.deleteUserContent', {
        name: row.display_name || row.email,
        email: row.email,
      }),
      okText: t('rbac.delete'),
      okType: 'danger',
      onOk: async () => {
        try {
          const { data, error } = await invokeEdgeFunction<{ error?: string; ok?: boolean }>(
            'provision-user',
            {
              body: {
                action: 'delete',
                user_id: row.auth_uid,
              },
            }
          );
          if (error) throw error;
          if (data && typeof data === 'object' && 'error' in data && data.error) {
            throw new Error(String(data.error));
          }
          message.success(t('rbac.userDeleted'));
          await afterMutation(null, load);
        } catch (e) {
          message.error(e instanceof Error ? e.message : t('rbac.deleteFailed'));
          return Promise.reject(e);
        }
      },
    });
  };

  const columns = [
    {
      title: t('rbac.email'),
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: t('rbac.displayName'),
      dataIndex: 'display_name',
      key: 'display_name',
      render: (v: string | null) => v || '—',
    },
    {
      title: t('rbac.identity'),
      dataIndex: 'identity_role',
      key: 'identity_role',
      render: (v: string) => (
        <Tag color={v === 'admin' ? 'geekblue' : 'green'}>{v}</Tag>
      ),
    },
    {
      title: t('rbac.permissionRole'),
      key: 'role',
      width: 240,
      render: (_: unknown, row: PortalUserRow) =>
        canManage ? (
          <Select
            style={{ width: 220 }}
            value={row.role_id ?? undefined}
            options={roles.map((r) => ({
              value: r.id,
              label: localizedRoleName(r.code, r.name, t),
            }))}
            onChange={(roleId) => void handleRoleChange(row.auth_uid, roleId)}
          />
        ) : (
          localizedRoleName(row.role_code, row.role_name, t)
        ),
    },
    {
      title: t('rbac.status'),
      key: 'status',
      render: (_: unknown, row: PortalUserRow) =>
        canManage ? (
          <Switch
            checked={row.status === 'active'}
            checkedChildren={t('rbac.active')}
            unCheckedChildren={t('rbac.disabled')}
            onChange={(checked) => void handleStatusToggle(row, checked)}
          />
        ) : (
          <Tag color={row.status === 'active' ? 'success' : 'default'}>
            {row.status}
          </Tag>
        ),
    },
    {
      title: t('rbac.lastSignIn'),
      dataIndex: 'last_sign_in_at',
      key: 'last_sign_in_at',
      render: (v: string | null) => (v ? new Date(v).toLocaleString() : '—'),
    },
    ...(canManage
      ? [
          {
            title: t('rbac.actions'),
            key: 'actions',
            width: 280,
            fixed: 'right' as const,
            render: (_: unknown, row: PortalUserRow) => (
              <Space wrap>
                <Button
                  type="link"
                  icon={<EditOutlined />}
                  onClick={() => openEdit(row)}
                >
                  {t('rbac.edit')}
                </Button>
                <Button
                  type="link"
                  icon={<KeyOutlined />}
                  onClick={() => handleSendSetupLink(row)}
                >
                  {t('rbac.sendSetupLink')}
                </Button>
                <Button
                  type="link"
                  danger
                  icon={<DeleteOutlined />}
                  disabled={row.auth_uid === user?.id}
                  onClick={() => handleDelete(row)}
                >
                  {t('rbac.delete')}
                </Button>
              </Space>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <AdminListPage
        title={t('rbac.usersTitle')}
        subtitle={t('rbac.usersSubtitle')}
        resultCount={users.length}
        extra={
          <PermissionGate permission="system.users.manage">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                form.resetFields();
                const superAdmin = roles.find((r) => r.code === 'super_admin');
                form.setFieldsValue({ role_id: superAdmin?.id });
                setCreateOpen(true);
              }}
            >
              {t('rbac.createHqAdmin')}
            </Button>
          </PermissionGate>
        }
      >
        <Table
          rowKey="auth_uid"
          loading={loading}
          dataSource={users}
          columns={columns}
          pagination={pagination}
          onChange={onTableChange}
          rowClassName={rowClassName}
          scroll={{ x: 1100 }}
          locale={{ emptyText: t('rbac.usersEmptyFleetHint') }}
        />
      </AdminListPage>

      <Modal
        title={t('rbac.createHqAdmin')}
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={() => void handleCreateHqAdmin()}
        confirmLoading={creating}
        okText={t('rbac.create')}
        cancelText={t('common.cancel')}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="email"
            label={t('rbac.email')}
            rules={[{ required: true }, { type: 'email' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="display_name" label={t('rbac.displayName')}>
            <Input />
          </Form.Item>
          <Form.Item
            name="role_id"
            label={t('rbac.permissionRole')}
            rules={[{ required: true }]}
          >
            <Select
              options={roles.map((r) => ({
                value: r.id,
                label: localizedRoleName(r.code, r.name, t),
              }))}
            />
          </Form.Item>
          <p style={{ color: 'rgba(0,0,0,0.45)', marginBottom: 0 }}>
            {t('rbac.tempPasswordHintSetup')}
          </p>
          <p style={{ color: 'rgba(0,0,0,0.45)', marginBottom: 0 }}>
            {t('rbac.identityFixedAdmin')}
          </p>
        </Form>
      </Modal>

      <Modal
        title={t('rbac.editUserTitle')}
        open={editOpen}
        onCancel={() => {
          setEditOpen(false);
          setEditingRow(null);
        }}
        onOk={() => void handleEditSave()}
        confirmLoading={savingEdit}
        okText={t('rbac.save')}
        cancelText={t('common.cancel')}
      >
        <Form form={editForm} layout="vertical">
          <Form.Item label={t('rbac.email')}>
            <Input value={editingRow?.email} disabled />
          </Form.Item>
          <Form.Item name="display_name" label={t('rbac.displayName')}>
            <Input />
          </Form.Item>
          <Form.Item
            name="role_id"
            label={t('rbac.permissionRole')}
            rules={[{ required: true }]}
          >
            <Select
              options={roles.map((r) => ({
                value: r.id,
                label: localizedRoleName(r.code, r.name, t),
              }))}
            />
          </Form.Item>
          <p style={{ color: 'rgba(0,0,0,0.45)', marginBottom: 0 }}>
            {t('rbac.identityFixedAdmin')}
          </p>
        </Form>
      </Modal>
    </>
  );
}

export default UsersPage;
